import { useEffect, useId, useMemo, useRef } from 'react'
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react'
import type { ChatActivity } from '../api/activity'
import { buildActivityPlot } from '../lib/activityPlot'
import { activitySampleAt, stepSampleIndex } from '../lib/activityInspection'
import type { ActivityInspectionSource, ActivityInspectionState } from '../lib/activityInspection'
import { formatAbsolute } from '../lib/format'

const rateFormat = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 3 })

export function ChatActivityGraph({ data, from, to, width, ticks, loading, inspection, onInspect, onDismiss }: {
  data: ChatActivity | null
  from: number
  to: number
  width: number
  ticks: number[]
  loading: boolean
  inspection: ActivityInspectionState | null
  onInspect: (timestampMs: number, source: ActivityInspectionSource) => void
  onDismiss: () => void
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const touchPointer = useRef(false)
  const pointerFocus = useRef(false)
  const patternId = `activity-gap-${useId().replace(/:/g, '')}`
  const instructionsId = `activity-instructions-${useId().replace(/:/g, '')}`
  const plot = useMemo(() => buildActivityPlot(data ?? { from, to, samples: [], sampleIntervalMs: 60000 }, width), [data, from, to, width])
  const gaps = data?.coverage.gaps ?? [{ from, to }]
  const x = (timestamp: number) => (timestamp - from) / (to - from) * plot.width
  const summary = plot.segments.length
    ? `DGG chat activity, one-minute averages in messages per second. Peak ${rateFormat.format(plot.peakRate)}. Striped regions have no data.`
    : loading ? 'Loading DGG chat activity' : 'DGG chat activity is unavailable for this window'
  const inspected = data && inspection ? activitySampleAt(data, inspection.timestampMs) : null
  const inspectionPercent = inspection ? Math.max(0, Math.min(100, (inspection.timestampMs - from) / (to - from) * 100)) : 0
  const readout = inspected
    ? `${formatAbsolute(inspected.timestampMs)}: ${inspected.messagesPerSecond === null ? 'No activity data' : `${rateFormat.format(inspected.messagesPerSecond)} msg/s`}`
    : ''

  const timestampAt = (clientX: number): number => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (!rect?.width) return from
    return from + Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * (to - from)
  }

  useEffect(() => {
    if (inspection?.source !== 'pinned') return
    const dismissOutside = (event: globalThis.PointerEvent) => {
      const target = event.target
      if (target instanceof Node && rootRef.current?.contains(target)) return
      onDismiss()
    }
    document.addEventListener('pointerdown', dismissOutside)
    return () => document.removeEventListener('pointerdown', dismissOutside)
  }, [inspection, onDismiss])

  const moveInspection = (event: PointerEvent<SVGSVGElement>) => {
    if (!data || inspection?.source === 'pinned' || event.pointerType === 'touch') return
    onInspect(activitySampleAt(data, timestampAt(event.clientX)).timestampMs, 'hover')
  }

  const handleKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    if (!data) return
    if (event.key === 'Escape') {
      onDismiss()
      return
    }
    const current = inspection?.timestampMs ?? data.samples.at(-1)?.timestampMs
    if (event.key === 'Home' || event.key === 'End') {
      if (data.samples.length === 0) return
      event.preventDefault()
      onInspect(data.samples[event.key === 'Home' ? 0 : data.samples.length - 1].timestampMs, 'keyboard')
      return
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const fallback = current ?? Math.floor((to - 1) / data.sampleIntervalMs) * data.sampleIntervalMs
    const index = stepSampleIndex(data.samples, fallback, event.key === 'ArrowLeft' ? -1 : 1)
    if (index >= 0) onInspect(data.samples[index].timestampMs, 'keyboard')
  }

  const handleClick = (event: MouseEvent<SVGSVGElement>) => {
    pointerFocus.current = false
    if (!data || !touchPointer.current) return
    touchPointer.current = false
    event.preventDefault()
    onInspect(activitySampleAt(data, timestampAt(event.clientX)).timestampMs, 'pinned')
  }

  return (
    <div ref={rootRef} className="timeline-activity-graph">
      <svg
        className="timeline-activity-svg"
        viewBox={`0 0 ${plot.width} ${plot.height}`}
        preserveAspectRatio="none"
        role="group"
        tabIndex={data ? 0 : -1}
        aria-label={summary}
        aria-describedby={data ? instructionsId : undefined}
        onPointerMove={moveInspection}
        onPointerDown={(event) => {
          pointerFocus.current = true
          touchPointer.current = event.pointerType === 'touch' || event.pointerType === 'pen'
        }}
        onPointerCancel={() => {
          pointerFocus.current = false
          touchPointer.current = false
        }}
        onPointerLeave={() => {
          pointerFocus.current = false
          if (inspection?.source === 'hover') onDismiss()
        }}
        onClick={handleClick}
        onFocus={() => {
          if (pointerFocus.current) {
            pointerFocus.current = false
            return
          }
          if (!data || inspection) return
          const latest = data.samples.at(-1)
          onInspect(latest?.timestampMs ?? Math.floor((to - 1) / data.sampleIntervalMs) * data.sampleIntervalMs, 'keyboard')
        }}
        onBlur={(event) => {
          if (inspection?.source !== 'keyboard') return
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onDismiss()
        }}
        onKeyDown={handleKeyDown}
      >
        <defs>
          <pattern id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
            <path d="M-2,2 L2,-2 M0,8 L8,0 M6,10 L10,6" className="timeline-activity-gap-stroke" />
          </pattern>
        </defs>
        {gaps.map((gap) => (
          <rect key={gap.from} className="timeline-activity-gap" x={x(gap.from)} y="0" width={x(gap.to) - x(gap.from)} height={plot.height} fill={`url(#${patternId})`} />
        ))}
        <g className="timeline-activity-grid">
          {ticks.map((tick) => <line key={tick} x1={x(tick)} x2={x(tick)} y1="0" y2={plot.height} />)}
          {[8, 50, plot.baseline].map((y) => <line key={y} x1="0" x2={plot.width} y1={y} y2={y} />)}
        </g>
        {plot.segments.map((segment) => (
          <g key={segment.points[0].timestampMs}>
            <path className="timeline-activity-area" d={segment.area} />
            <path className="timeline-activity-line" d={segment.line} vectorEffect="non-scaling-stroke" />
          </g>
        ))}
      </svg>
      <span id={instructionsId} className="sr-only">Use the left and right arrow keys to inspect one-minute samples. Press Escape to dismiss.</span>
      <span className="sr-only" role="status" aria-live="polite">{inspection?.source === 'keyboard' ? readout : ''}</span>
      {inspected && (
        <div
          className={`timeline-activity-readout${inspection?.source === 'pinned' ? ' is-pinned' : ''}`}
          style={{ left: `clamp(8px, calc(${inspectionPercent}% - 8px), max(8px, calc(100% - 220px)))` }}
          aria-hidden="true"
        >
          <strong>{formatAbsolute(inspected.timestampMs)}</strong>
          <span>{inspected.messagesPerSecond === null ? 'No activity data' : `${rateFormat.format(inspected.messagesPerSecond)} msg/s`}</span>
        </div>
      )}
      {plot.segments.length > 0 ? (
        <>
          <span className="timeline-activity-scale is-max" aria-hidden="true">{rateFormat.format(plot.scaleMax)} msg/s</span>
          <span className="timeline-activity-scale is-zero" aria-hidden="true">0</span>
        </>
      ) : (
        <span className="timeline-activity-placeholder" aria-hidden="true">{loading ? 'Loading activity…' : 'No activity data in this window'}</span>
      )}
      <div className="timeline-now" />
    </div>
  )
}
